import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { UsersService } from 'src/app/services/users/users.service';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, lastValueFrom } from 'rxjs';
import { Router } from '@angular/router';
import { UserInfo } from '../../models/user-info.model';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { KeycloakService } from 'src/app/services/keycloak/keycloak.service';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { ProgressSpinnerMode } from '@angular/material/progress-spinner';
import { entAnimations } from 'src/app/animations';
import { environment } from 'src/environments/environment';
import { Error } from 'src/app/models/error.model';
import { ImageService } from 'src/app/services/profile-picture/images.service';
import { ImageUploadDialogComponent } from '../image-upload-dialog/image-upload-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { ImageDeleteDialogComponent } from '../image-upload-dialog/image-delete-dialog/image-delete-dialog.component';
import { RouteStatusService } from 'src/app/services/maintenance/route-status.service';
import { Subscription } from 'rxjs';
import { CustomValidators } from 'src/app/utils/validators';
import { LocalStorageService } from 'src/app/services/localStorage/localStorage.service';
import { UserSelfPasswordChange } from 'src/app/models/user-password-change.model';
import { UserRecoveryInfos } from 'src/app/models/user-recovery-infos.model';

@Component({
  selector: 'app-self',
  templateUrl: './self.component.html',
  styleUrls: ['./self.component.scss'],
  encapsulation: ViewEncapsulation.None,
  animations: entAnimations,
  standalone: false,
})
export class SelfComponent implements OnInit {
  // API Calls
  userOid: string;
  isLoading = false;
  isLoadingName = false;
  isStudent = false;

  selectedUser: UserInfo = new UserInfo();
  updatedUser: UserInfo = new UserInfo();
  errorUpdate = false;
  isErrorUpdateName = false;
  isErrorUpdatePwd = false;
  isErrorUpdateOtp = false;
  isBusy = false;
  isBusyUpdateName = false;
  isBusyUpdatePwd = false;
  isBusyUpdateOtp = false;
  isAvsAlign = false;
  error: Error = { error: { message: '', code: '' } };
  errorUpdateName: Error = { error: { message: '', code: '' } };
  errorUpdatePwd: Error = { error: { message: '', code: '' } };
  errorUpdateOtp: Error = { error: { message: '', code: '' } };

  selectedFileUrl: string | null = null;

  spinner = {
    color: 'primary',
    mode: 'indeterminate' as ProgressSpinnerMode,
    value: 50,
    diameter: 50,
  };

  // Profile Form
  profilform: FormGroup;
  hiderequired = false;
  isUserUpdated = false;

  preferedLanguage = [
    {
      id: 1,
      name: 'Français',
      acronym: 'fr',
      lang: 'fr',
    },
    {
      id: 2,
      name: 'Deutsch',
      acronym: 'de',
      lang: 'de',
    },
  ];

  userlangage: string;
  usergender: string;

  private genderFR = [
    {
      id: 1,
      name: 'Homme',
      acronym: 'M',
    },
    {
      id: 2,
      name: 'Femme',
      acronym: 'F',
    },
  ];

  private genderDE = [
    {
      id: 1,
      name: 'Mann',
      acronym: 'M',
    },
    {
      id: 2,
      name: 'Frau',
      acronym: 'F',
    },
  ];

  // Pwd
  securityform: FormGroup;
  isPwdUpdated = false;
  timeLeft = 30;
  interval: NodeJS.Timeout;
  isPasswordChangeDisabled = true;
  isUploadImageDisabled = false;
  private subscription: Subscription = new Subscription();

  private passwordUpdateDate = new BehaviorSubject<Date>(null);
  lastPasswordModification$ = this.passwordUpdateDate.asObservable();

  // Change Otp
  optform: FormGroup;
  isOtpUpdated = false;

  // Tabs
  selectedIndex = 0;

  // Validators
  phoneValidator = '^[0-9]*$';
  maxlength = 9;

  errorAccess = false;

  imageError: string;
  isImageSaved: boolean;
  cardImageBase64: string;
  mustChangemail: boolean;

  constructor(
    private _userService: UsersService,
    private _translateService: TranslateService,
    private _formBuilder: FormBuilder,
    private _router: Router,
    private _keycloackService: KeycloakService,
    private _imageService: ImageService,
    private _dialog: MatDialog,
    private _routeStatusService: RouteStatusService,
    private _localStorageService: LocalStorageService
  ) {}

  ngOnInit() {
    this.isLoading = false;
    this.isBusy = false;

    this.getUser();

    this.securityform = this._formBuilder.group(
      {
        oldpassword: ['', [Validators.required]],
        password: [
          '',
          [
            Validators.required,
            CustomValidators.passwordInterdiction(),
            CustomValidators.passwordRestriction(),
            CustomValidators.passwordNameValidator(
              this.selectedUser.officialFirstName,
              this.selectedUser.officialLastName
            ),
            CustomValidators.passwordSameAccountNameValidator(
              this.selectedUser.samAccountName.toLowerCase()
            ),
            Validators.minLength(10),
            Validators.maxLength(50),
          ],
        ],
        passwordConfirmation: ['', [Validators.required]],
      },
      { validators: [CustomValidators.confirmPasswords()] }
    );

    // Maintenance mode settings
    const urls = ['/password-change', '/image-upload'];
    this.subscription.add(
      this._routeStatusService
        .getRouteStatusForUrls(urls)
        .subscribe(statuses => {
          // Retrieve the statuses or default to false if not found
          this.isPasswordChangeDisabled = statuses['/password-change'];
          this.isUploadImageDisabled = statuses['/image-upload'];

          if (this.isPasswordChangeDisabled) {
            this.securityform.disable();
          } else {
            this.securityform.enable();
          }
        })
    );

    this.optform = this._formBuilder.group({
      password_otp: [
        '',
        [
          Validators.required,
          CustomValidators.passwordRestriction(),
          Validators.minLength(10),
          Validators.maxLength(50),
        ],
      ],
      phone: [
        '',
        [
          Validators.required,
          Validators.pattern(this.phoneValidator),
          CustomValidators.phoneValidation(),
          Validators.minLength(9),
        ],
      ],
      defaultDial: [''],
      email: [
        '',
        [
          Validators.required,
          CustomValidators.validEmail(),
          CustomValidators.EmailInterdiction(),
        ],
      ],
    });

    this.profilform = this._formBuilder.group({
      avs: [{ value: '', disabled: true }],
      dateOfBirth: [{ value: '', disabled: true }],
      gender: [{ value: '', disabled: true }],
      firstName: [''],
      lastName: [''],
      address: [{ value: '', disabled: true }],
      zip: [{ value: '', disabled: true }],
      locality: [{ value: '', disabled: true }],
      preferredLanguage: ['', Validators.required],
    });

    this.setDefaultValue();

    this._userService
      .getUserCredentials(this.selectedUser.oid)
      .subscribe(value => this.passwordUpdateDate.next(value));
  }

  setDefaultValue(): void {
    this.mustChangemail = false;
    if (
      this.selectedUser.email.includes('@edu.vs.ch') ||
      this.selectedUser.email.includes('educanet2.ch')
    ) {
      this.mustChangemail = true;
    }

    // Set Default value for Step 1
    this.profilform.get('avs').setValue(this.selectedUser.avs);
    this.profilform.get('dateOfBirth').setValue(this.selectedUser.dateOfBirth);
    this.profilform.get('gender').setValue(this.selectedUser.gender);

    if (this.selectedUser.preferredLanguage === 'de') {
      const genderType = this.genderDE.find(
        gender => gender.acronym === this.selectedUser.gender
      );
      this.profilform.get('gender').setValue(genderType.name);
      this.usergender = genderType.name;
    } else if (this.selectedUser.preferredLanguage === 'fr') {
      const genderType = this.genderFR.find(
        gender => gender.acronym === this.selectedUser.gender
      );
      this.profilform.get('gender').setValue(genderType.name);
      this.usergender = genderType.name;
    }

    this.profilform.get('firstName').setValue(this.selectedUser.firstName);
    this.profilform.get('lastName').setValue(this.selectedUser.lastName);

    this.profilform.get('address').setValue(this.selectedUser.address);
    this.profilform.get('zip').setValue(this.selectedUser.zip);
    this.profilform.get('locality').setValue(this.selectedUser.locality);

    // Get Default Language
    const toSelect = this.preferedLanguage.find(
      lang => lang.acronym === this.selectedUser.preferredLanguage
    );

    this.userlangage = toSelect.name;
    this.profilform.get('preferredLanguage').setValue(toSelect);

    // OTP Form
    if (this.selectedUser.phone !== '') {
      this.optform
        .get('defaultDial')
        .setValue(this.selectedUser.phone.substring(0, 3));
    } else {
      this.optform.get('defaultDial').setValue('+41');
    }

    this.optform
      .get('phone')
      .setValue(
        this.selectedUser.phone.substring(3, this.selectedUser.phone.length)
      );
    this.optform.get('email').setValue(this.selectedUser.email);

    if (this.selectedUser.twoFactorAuthType === 'Token') {
      this.hiderequired = true;
      this.optform
        .get('phone')
        .setValidators([
          Validators.pattern(this.phoneValidator),
          CustomValidators.phoneValidation(),
          Validators.minLength(9),
        ]);
    }
  }

  fetchSecurityFormData(): UserSelfPasswordChange {
    return {
      password: this.securityform.get('oldpassword').value,
      newPassword: this.securityform.get('password').value,
    };
  }

  fetchProfileFormData(): void {
    const user: UserInfo = new UserInfo();

    user.id = this.selectedUser.id;
    user.oid = this.selectedUser.oid;

    if (this.profilform.get('address').value != '') {
      user.address = this.profilform.get('address').value;
    }

    if (this.profilform.get('zip').value != '') {
      user.zip = this.profilform.get('zip').value;
    }
    if (this.profilform.get('locality').value != '') {
      user.locality = this.profilform.get('locality').value;
    }
    this.updatedUser = user;

    // Reset all errors and boolean in case of multiple error
    this.isBusy = false;
    this.isUserUpdated = false;
    this.errorUpdate = false;
  }

  fetchOTPFormData(): UserRecoveryInfos {
    //Fill in this data as it will be used shortly after the update request.
    const user: UserInfo = new UserInfo();
    if (this.optform.get('phone').value != '') {
      user.phone =
        this.optform.get('defaultDial').value + this.optform.get('phone').value;
    }
    user.email = this.optform.get('email').value;
    user.firstName = this.selectedUser.firstName;
    user.lastName = this.selectedUser.lastName;
    this.updatedUser = user;

    return {
      email: user.email,
      password: this.optform.get('password_otp').value,
      phone: user.phone,
      username: this.selectedUser.username,
    };
  }

  changeEmailLink(): void {
    this.selectedIndex = 13;
  }

  getEmailErrorMessage(): string {
    return this.optform.get('email').hasError('pattern')
      ? this._translateService.instant('ERROR.EMAIL_VALID')
      : this._translateService.instant('ERROR.EMAIL_SCHOOL');
  }

  getErrorMessage(): string {
    return this.securityform.get('password').hasError('required')
      ? this._translateService.instant('ERROR.REQUIRED')
      : this.securityform.get('password').hasError('passwordRestriction')
        ? this._translateService.instant('ERROR.COMPLEXITY')
        : this.securityform.get('password').hasError('oldpasswordmatch')
          ? this._translateService.instant('ERROR.INTERDICTION')
          : this.securityform.get('password').hasError('passwordContainName')
            ? this._translateService.instant('ERROR.CONTAINNAME')
            : this.securityform.get('password').hasError('minlength')
              ? this._translateService.instant('ERROR.MIN')
              : this.securityform
                    .get('password')
                    .hasError('passwordContainSamAccountName')
                ? this._translateService.instant('ERROR.CONTAINSAMACCOUNTNAME')
                : this.securityform.get('password').hasError('maxlength')
                  ? this._translateService.instant('ERROR.MAX')
                  : '';
  }

  getErrorMessageOldPassword(): string {
    return this.securityform.get('oldpassword').hasError('required')
      ? this._translateService.instant('ERROR.REQUIRED')
      : this.securityform.get('oldpassword').hasError('passwordRestriction')
        ? this._translateService.instant('ERROR.COMPLEXITY')
        : this.securityform.get('oldpassword').hasError('oldpasswordmatch')
          ? this._translateService.instant('ERROR.INTERDICTION')
          : this.securityform.get('oldpassword').hasError('passwordContainName')
            ? this._translateService.instant('ERROR.CONTAINNAME')
            : this.securityform.get('oldpassword').hasError('minlength')
              ? this._translateService.instant('ERROR.MIN')
              : this.securityform.get('oldpassword').hasError('maxlength')
                ? this._translateService.instant('ERROR.MAX')
                : '';
  }

  getErrorMessageOTP(): string {
    return this.optform.get('password_otp').hasError('required')
      ? this._translateService.instant('ERROR.REQUIRED')
      : this.optform.get('password_otp').hasError('passwordRestriction')
        ? this._translateService.instant('ERROR.COMPLEXITY')
        : this.optform.get('password_otp').hasError('minlength')
          ? this._translateService.instant('ERROR.MIN')
          : this.optform.get('password_otp').hasError('maxlength')
            ? this._translateService.instant('ERROR.MAX')
            : '';
  }

  tabChanged(tabChangeEvent: MatTabChangeEvent): void {
    this.selectedIndex = tabChangeEvent.index;
  }

  startTimer() {
    this.interval = setInterval(() => {
      if (this.timeLeft > 0) {
        this.timeLeft--;
      } else {
        this._userService.emptyStorage();
        this._keycloackService.logout(environment.logoutURL);
        this._keycloackService.clearToken();
      }
    }, 1000);
  }

  async changePwd(): Promise<void> {
    this.errorUpdatePwd = { error: { message: '', code: '' } };
    this.isErrorUpdatePwd = false;
    this.isPwdUpdated = false;

    try {
      // Reset all errors and boolean in case of multiple error
      this.isBusy = false;
      this.isUserUpdated = false;
      this.errorUpdate = false;

      this.isBusyUpdatePwd = true;
      await lastValueFrom(
        this._userService.changePassword(
          this.selectedUser.oid,
          this.fetchSecurityFormData()
        )
      ).then(() => {
        this.isPwdUpdated = true;
      });
    } catch (error) {
      let customError: Error = { error: undefined };

      if (error.statusText == 'Unknown Error') {
        customError = { error: { message: 'Unknown Error', code: 'ERR2' } };
      } else {
        if (error.error) {
          customError = error.error;
        }
        if (customError.error === undefined) {
          customError = { error: { message: 'Unknown Error', code: 'ERR2' } };
        }
      }

      this.isErrorUpdatePwd = true;
      this.errorUpdatePwd = customError;
      this.isPwdUpdated = false;
    } finally {
      this.isBusyUpdatePwd = false;
      this.passwordUpdateDate.next(new Date());
    }
  }

  async updateUserOTP(): Promise<void> {
    this.errorUpdateOtp = { error: { message: '', code: '' } };
    this.isErrorUpdateOtp = false;
    this.isOtpUpdated = false;
    this.isBusyUpdateOtp = true;

    try {
      const request = this.fetchOTPFormData();
      await lastValueFrom(
        this._userService.updateRecoveryInfos(this.selectedUser.oid, request)
      ).then(() => {
        this._userService.userHasBeenUpdated(
          this.updatedUser.firstName,
          this.updatedUser.lastName,
          this.updatedUser.email
        );
        this.isOtpUpdated = true;
      });
      this.selectedUser = await lastValueFrom(
        this._userService.getCurrentUser()
      );
      this.selectedUser.email = this.updatedUser.email;
      this.selectedUser.phone = this.updatedUser.phone;
    } catch (error) {
      let customError: Error = { error: undefined };

      if (error.statusText == 'Unknown Error') {
        customError = { error: { message: 'Unknown Error', code: 'ERR2' } };
      } else {
        if (error.error) {
          customError = error.error;
        }
        if (customError.error === undefined) {
          customError = { error: { message: 'Unknown Error', code: 'ERR2' } };
        }
      }
      this.isErrorUpdateOtp = true;
      this.errorUpdateOtp = customError;
      this.isOtpUpdated = false;
    } finally {
      this.optform.get('password_otp').reset();
      this.isBusyUpdateOtp = false;
    }
  }

  async getUser(): Promise<void> {
    try {
      this.isLoading = true;
      // NO User in Cache
      if (this._localStorageService.getItem('currentUser') === null) {
        this.selectedUser = await lastValueFrom(
          this._userService.getCurrentUser()
        );
      } else {
        this.selectedUser = this._userService.currentUserValue;
      }

      this.selectedFileUrl = await lastValueFrom(
        this._imageService.getProfilePicture(this.selectedUser.oid)
      );

      this.isLoading = false;
      this.userOid = this.selectedUser.oid;
      this.isAvsAlign = this._userService.checkIsAvsAlign(this.selectedUser);
      this.selectedUser.roles.forEach(element => {
        if (element.name.toLowerCase().includes('eleve')) {
          this.isStudent = true;
        }
      });
    } catch (error) {
      let customerError: Error = { error: undefined };
      if (error.statusText == 'Unknown Error') {
        customerError = { error: { message: 'Unknown Error', code: 'ERR2' } };
      } else {
        if (error.error) {
          customerError = error.error;
        }
        if (customerError.error !== undefined) {
          if (customerError.error.code == 'ERR403') {
            this._router.navigate(['/errors/error-403']);
          } else {
            this._router.navigate(['/errors/error-404']);
          }
        }
      }
    }
  }

  checkPermission(): boolean {
    return this._imageService.checkPermissionAddImageSelf(this.selectedUser);
  }

  // Dialog add image
  openDialog(): void {
    // const buttonElement = document.activeElement as HTMLElement; // Get the currently focused element
    // buttonElement.blur(); // Remove focus from the button
    const dialogRef = this._dialog.open(ImageUploadDialogComponent, {
      width: 'auto',
      height: 'auto',
      maxWidth: 'none',
      maxHeight: 'none',
      data: {
        userOid: this.userOid,
        isSelf: true,
        selectedFileUrl: this.selectedFileUrl,
      },
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result !== undefined) this.selectedFileUrl = result;
    });
  }

  // Dialog rmeove image
  openDialogDelete(): void {
    const dialogRef = this._dialog.open(ImageDeleteDialogComponent, {
      width: 'auto',
      height: 'auto',
      maxWidth: 'none',
      maxHeight: 'none',
      data: {
        userOid: this.userOid,
        isSelf: true,
        selectedFileUrl: this.selectedFileUrl,
      },
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result !== undefined) this.selectedFileUrl = result;
    });
  }
}
